import React, { useState, useRef } from 'react';
import { C } from '../tokens';
import Icon from '../components/Icon';
import { Card, Pill, Btn, Breadcrumb } from '../components/UI';
import useBreakpoint from '../useBreakpoint';

/* ═══════════════════════════════════════════════════════════
   Static Data
   ═══════════════════════════════════════════════════════════ */
const AGENTS = [
  { icon: 'support_agent', label: 'Customer Service',  desc: 'Optimization for ticket resolution and empathy.' },
  { icon: 'payments',      label: 'Sales Assistant',   desc: 'Focused on lead conversion and booking CTAs.' },
  { icon: 'construction',  label: 'Technical Support', desc: 'Engineered for troubleshooting and documentation.' },
  { icon: 'concierge',     label: 'General Concierge', desc: 'Versatile conversationalist for all-round tasks.' },
];

const TABS = ['Basic Info', 'Knowledge & RAG', 'Behavior & Prompt', 'Integrations'];

const INITIAL_SOURCES = [
  { id: 1, icon: 'picture_as_pdf', iconColor: C.red,   name: 'company_handbook.pdf', sub: 'Last updated: 2 hours ago • 1.2 MB', status: 'Indexed', statusColor: C.green, type: 'file',      url: '' },
  { id: 2, icon: 'database',       iconColor: C.indigo, name: 'customer_db_v2 (SQL)', sub: 'Syncing live connector • 45k rows',  status: 'Syncing', statusColor: C.amber, type: 'database',  url: 'postgresql://db.company.io:5432/customers' },
  { id: 3, icon: 'code',           iconColor: C.teal,   name: 'api_docs.html',        sub: 'Static web crawl • 12 pages',       status: 'Indexed', statusColor: C.green, type: 'web_crawl', url: 'https://docs.company.io/api' },
];

const CHANNELS = [
  { id: 'website',  name: 'Website Widget',     desc: 'Live on your personal and business domains',        icon: 'language',      iconBg: '#ecfdf5', iconColor: '#059669', active: true,  badge: null },
  { id: 'whatsapp', name: 'WhatsApp Business',  desc: 'Automated replies for WhatsApp Business API',       icon: 'chat',          iconBg: '#e8e2df', iconColor: '#888',    active: false, badge: 'Coming Soon' },
  { id: 'mobile',   name: 'Mobile SDK',         desc: 'Native iOS and Android integration tools',          icon: 'phone_iphone',  iconBg: '#e8e2df', iconColor: '#888',    active: false, badge: 'Enterprise Only' },
  { id: 'slack',    name: 'Slack',              desc: 'Push notifications and team alert channels',        icon: 'tag',           iconBg: '#e8e2df', iconColor: '#888',    active: false, badge: 'Coming Soon' },
  { id: 'telegram', name: 'Telegram Bot',       desc: 'Deploy your agent as a Telegram chatbot',           icon: 'send',          iconBg: '#e8e2df', iconColor: '#888',    active: false, badge: 'Coming Soon' },
];

/* ═══════════════════════════════════════════════════════════
   Shared form styles
   ═══════════════════════════════════════════════════════════ */
const inputStyle = {
  width: '100%', padding: '12px 16px', borderRadius: 10,
  border: `1px solid ${C.bdr}`, fontSize: 13, outline: 'none',
  fontFamily: 'inherit', background: '#faf9f8',
  transition: 'border .15s, box-shadow .15s, background .15s',
  boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 700, color: C.t1, marginBottom: 8 };
const focusHandlers = {
  onFocus: e => { e.target.style.borderColor = C.primary; e.target.style.boxShadow = `0 0 0 3px ${C.pL}`; e.target.style.background = C.w; },
  onBlur:  e => { e.target.style.borderColor = C.bdr;     e.target.style.boxShadow = 'none';              e.target.style.background = '#faf9f8'; },
};

const FOOTER_HEIGHT = 64;

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT — All state lives here
   ═══════════════════════════════════════════════════════════ */
export default function SettingsPage() {
  const { isMobile, isTablet, isDesktop, isWide } = useBreakpoint();
  const mob = isMobile;
  const compact = isMobile || isTablet;
  const pad = mob ? 16 : isTablet ? 24 : 36;

  const [tab, setTab]     = useState(0);
  const [agent, setAgent] = useState(0);

  /*
   * ★ CRITICAL: These are the single source of truth.
   *   Both the form inputs AND the live preview read from these.
   *   Typing in the form → setState → preview re-renders instantly.
   */
  const [botName, setBotName]       = useState('Sari');
  const [welcomeMsg, setWelcomeMsg] = useState("Hi! I'm Sari, how can I help you today?");
  const [avatarUrl, setAvatarUrl]   = useState(null);

  const avatarInputRef = useRef(null);
  const [saved, setSaved] = useState(false);

  const handleAvatarUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUrl(URL.createObjectURL(file));
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ─── Scrollable content ─── */}
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: FOOTER_HEIGHT }}>
        <div style={{ padding: `${mob ? 16 : 32}px ${pad}px 24px` }}>
          {!mob && <Breadcrumb items={['Console', 'AI Settings']} />}
          <h2 style={{ fontSize: mob ? 22 : 28, fontWeight: 900, letterSpacing: '-.03em', marginBottom: mob ? 20 : 28, marginTop: 2 }}>
            AI Settings
          </h2>

          {/* ─── Agent Type Selector ─── */}
          <div style={{ marginBottom: mob ? 20 : 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 4 }}>
              <h3 style={{ fontSize: mob ? 15 : 17, fontWeight: 700 }}>Agent Type Selector</h3>
              {!mob && <span style={{ fontSize: 13, color: C.t2 }}>Pick a template for your core AI behavior</span>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: mob ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: mob ? 10 : 14 }}>
              {AGENTS.map((a, i) => {
                const sel = agent === i;
                return (
                  <div key={i} onClick={() => setAgent(i)} style={{
                    position: 'relative', padding: mob ? 12 : 16, borderRadius: 14, cursor: 'pointer',
                    transition: 'all .2s',
                    border: sel ? `2px solid ${C.primary}` : `1px solid ${C.bdr}`,
                    background: sel ? `${C.primary}06` : C.w,
                    display: 'flex', flexDirection: 'column', gap: mob ? 8 : 12,
                  }}>
                    {sel && (
                      <Icon name="check_circle" size={mob ? 18 : 22} style={{ position: 'absolute', top: 8, right: 8, color: C.primary }} />
                    )}
                    <div style={{ width: mob ? 36 : 46, height: mob ? 36 : 46, borderRadius: 10, background: sel ? C.pL : '#f1eeec', display: 'grid', placeItems: 'center' }}>
                      <Icon name={a.icon} size={mob ? 20 : 26} style={{ color: sel ? C.primary : C.t2 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: mob ? 12 : 14, fontWeight: 700, marginBottom: mob ? 2 : 4 }}>{a.label}</div>
                      {!mob && <div style={{ fontSize: 12, color: C.t2, lineHeight: 1.5 }}>{a.desc}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ─── Tabs ─── */}
          <div style={{ borderBottom: `1px solid ${C.bdr}`, display: 'flex', gap: mob ? 0 : 32, marginBottom: mob ? 20 : 28, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {TABS.map((t, i) => (
              <button key={t} onClick={() => setTab(i)} style={{
                padding: mob ? '10px 12px' : '14px 2px', fontSize: mob ? 12 : 13, whiteSpace: 'nowrap',
                fontWeight: tab === i ? 700 : 500,
                color: tab === i ? C.primary : C.t2,
                background: 'none', border: 'none',
                borderBottom: `2px solid ${tab === i ? C.primary : 'transparent'}`,
                cursor: 'pointer',
              }}>{t}</button>
            ))}
          </div>

          {/* ─── Tab content ─── */}

          {/* ★ BASIC INFO — form + preview side by side, sharing state */}
          {tab === 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: isWide ? '1fr 340px' : '1fr', gap: mob ? 16 : 28, alignItems: 'start' }}>

              {/* ══ LEFT: Chatbot Identity Form ══ */}
              <Card style={{ overflow: 'hidden' }}>
                <div style={{ padding: '24px 28px', borderBottom: `1px solid ${C.bdr}` }}>
                  <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Chatbot Identity</h4>
                  <p style={{ fontSize: 13, color: C.t2 }}>Define how your AI looks and communicates with your visitors.</p>
                </div>

                <div style={{ padding: '28px 28px 32px', display: 'flex', flexDirection: 'column', gap: 28 }}>
                  {/* Avatar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <div style={{
                        width: 88, height: 88, borderRadius: '50%', overflow: 'hidden',
                        border: '4px solid #f1eeec', background: '#f1eeec',
                        display: 'grid', placeItems: 'center',
                      }}>
                        {avatarUrl
                          ? <img src={avatarUrl} alt="Bot" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <Icon name="smart_toy" size={40} style={{ color: C.teal }} />
                        }
                      </div>
                      <button onClick={() => avatarInputRef.current?.click()} style={{
                        position: 'absolute', bottom: -2, right: -2,
                        width: 30, height: 30, borderRadius: '50%',
                        background: C.w, border: '1px solid #f1eeec',
                        boxShadow: '0 2px 8px rgba(0,0,0,.1)',
                        display: 'grid', placeItems: 'center', cursor: 'pointer', color: C.primary,
                      }}>
                        <Icon name="edit" size={16} />
                      </button>
                      <input ref={avatarInputRef} type="file" accept="image/*"
                        style={{ display: 'none' }} onChange={handleAvatarUpload} />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 8, color: C.t1 }}>Bot Avatar</label>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                        <button onClick={() => avatarInputRef.current?.click()} style={{
                          padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                          cursor: 'pointer', background: '#f1eeec', border: 'none', color: C.t1,
                          transition: 'background .15s',
                        }}
                          onMouseEnter={e => e.currentTarget.style.background = '#e8e2df'}
                          onMouseLeave={e => e.currentTarget.style.background = '#f1eeec'}>
                          Upload New
                        </button>
                        {avatarUrl && (
                          <button onClick={() => setAvatarUrl(null)} style={{
                            padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                            cursor: 'pointer', background: 'none', border: `1px solid ${C.bdr}`,
                            color: C.red, transition: 'background .15s',
                          }}
                            onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            Remove
                          </button>
                        )}
                      </div>
                      <p style={{ fontSize: 10, color: C.t3 }}>JPG, PNG or SVG. Max size 2MB.</p>
                    </div>
                  </div>

                  {/* Display Name — controlled input */}
                  <div>
                    <label style={labelStyle}>Display Name</label>
                    <input
                      value={botName}
                      onChange={e => setBotName(e.target.value)}
                      placeholder="Enter bot name…"
                      style={inputStyle}
                      {...focusHandlers}
                    />
                  </div>

                  {/* Welcome Message — controlled textarea */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 8 }}>
                      <label style={{ fontSize: 13, fontWeight: 700, color: C.t1 }}>Welcome Message</label>
                      <span style={{ fontSize: 10, fontWeight: 700, color: C.t3 }}>{welcomeMsg.length} / 500</span>
                    </div>
                    <textarea
                      value={welcomeMsg}
                      onChange={e => { if (e.target.value.length <= 500) setWelcomeMsg(e.target.value); }}
                      placeholder="Write the first message your bot will send…"
                      rows={4}
                      style={{ ...inputStyle, resize: 'none', lineHeight: 1.6 }}
                      {...focusHandlers}
                    />
                  </div>
                </div>
              </Card>

              {/* ══ RIGHT: Live Preview — reads SAME state ══ */}
              {isWide && <div style={{ position: 'sticky', top: 0 }}>
                <h3 style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 14 }}>
                  Live Preview
                </h3>
                <div style={{
                  background: C.w, border: `1px solid ${C.bdr}`, borderRadius: 20,
                  overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,.08)',
                  display: 'flex', flexDirection: 'column', height: 540,
                }}>
                  {/* ── Preview: Chat header ── */}
                  <div style={{
                    background: C.teal, padding: '14px 16px',
                    display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
                  }}>
                    {/* Avatar — synced with avatarUrl state */}
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,.3)',
                      overflow: 'hidden', background: 'rgba(255,255,255,.15)',
                      display: 'grid', placeItems: 'center', flexShrink: 0,
                    }}>
                      {avatarUrl
                        ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <Icon name="smart_toy" size={22} style={{ color: '#fff' }} />
                      }
                    </div>
                    <div style={{ flex: 1 }}>
                      {/* Name — synced with botName state */}
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                        {botName || 'Bot Name'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80' }} />
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,.8)' }}>Online</span>
                      </div>
                    </div>
                    <Icon name="close" size={20} style={{ color: 'rgba(255,255,255,.7)', cursor: 'pointer' }} />
                  </div>

                  {/* ── Preview: Chat body ── */}
                  <div style={{ flex: 1, padding: 16, background: '#f8fafb', overflowY: 'auto' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      {/* Small avatar */}
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: `${C.teal}18`, overflow: 'hidden',
                        display: 'grid', placeItems: 'center', flexShrink: 0, marginTop: 2,
                      }}>
                        {avatarUrl
                          ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                          : <Icon name="smart_toy" size={16} style={{ color: C.teal }} />
                        }
                      </div>
                      {/* Welcome message bubble — synced with welcomeMsg state */}
                      <div style={{
                        background: C.teal, color: '#fff',
                        padding: '10px 14px',
                        borderRadius: '16px 16px 16px 4px',
                        fontSize: 13, lineHeight: 1.5,
                        maxWidth: '80%',
                        boxShadow: '0 1px 4px rgba(0,0,0,.08)',
                        wordBreak: 'break-word',
                        transition: 'all .15s ease',
                      }}>
                        {welcomeMsg || (
                          <span style={{ opacity: 0.6, fontStyle: 'italic' }}>Type a welcome message…</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ── Preview: Chat input ── */}
                  <div style={{ padding: '12px 16px', borderTop: `1px solid ${C.bdr}`, background: C.w, flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f1eeec', borderRadius: 99, padding: '10px 16px' }}>
                      <span style={{ flex: 1, fontSize: 12, color: C.t3 }}>Type a message…</span>
                      <Icon name="send" size={16} style={{ color: C.t3 }} />
                    </div>
                    <p style={{ fontSize: 9, textAlign: 'center', color: C.t3, marginTop: 8 }}>Powered by Nieramind.AI</p>
                  </div>
                </div>
              </div>}
            </div>
          )}

          {tab === 1 && <KnowledgeTab mob={mob} compact={compact} />}
          {tab === 2 && <BehaviorTab mob={mob} compact={compact} />}
          {tab === 3 && <IntegrationsTab mob={mob} compact={compact} />}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════
          FIXED FOOTER
         ═══════════════════════════════════════════════════════ */}
      <div style={{
        height: mob ? 56 : FOOTER_HEIGHT, flexShrink: 0,
        background: C.w, borderTop: `1px solid ${C.bdr}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: `0 ${pad}px`, gap: 8,
      }}>
        {/* Left: Discard + Auto-save */}
        <div style={{ display: 'flex', alignItems: 'center', gap: mob ? 8 : 20 }}>
          <button style={{
            fontSize: mob ? 11 : 13, fontWeight: 700, color: C.t1, cursor: 'pointer',
            padding: mob ? '6px 12px' : '8px 18px', borderRadius: 8,
            border: `1px solid ${C.bdr}`, background: C.w,
            transition: 'background .15s',
          }}
            onMouseEnter={e => e.currentTarget.style.background = '#f5f3f2'}
            onMouseLeave={e => e.currentTarget.style.background = C.w}>
            {mob ? 'Discard' : 'Discard Changes'}
          </button>
          {!mob && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.teal, animation: 'pulse 2s ease-in-out infinite' }} />
            <span style={{ fontSize: 12, color: C.t2, fontWeight: 500 }}>Auto-save active</span>
          </div>}
        </div>

        {/* Right: Save + Publish */}
        <div style={{ display: 'flex', alignItems: 'center', gap: mob ? 8 : 12 }}>
          {saved && (
            <span style={{ fontSize: 12, fontWeight: 600, color: C.green, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icon name="check_circle" size={16} style={{ color: C.green }} />
            </span>
          )}
          {!mob && <button onClick={handleSave} style={{
            padding: '10px 24px', borderRadius: 10, fontSize: 13, fontWeight: 700,
            cursor: 'pointer', border: `2px solid ${C.primary}`,
            background: C.w, color: C.primary, transition: 'all .15s',
          }}
            onMouseEnter={e => e.currentTarget.style.background = `${C.primary}08`}
            onMouseLeave={e => e.currentTarget.style.background = C.w}>
            Save Progress
          </button>}
          <button onClick={handleSave} style={{
            padding: mob ? '8px 16px' : '10px 24px', borderRadius: 10, fontSize: mob ? 12 : 13, fontWeight: 700,
            cursor: 'pointer', border: 'none', background: C.primary, color: '#fff',
            boxShadow: `0 4px 14px ${C.primary}33`,
            display: 'flex', alignItems: 'center', gap: 6, transition: 'opacity .15s',
          }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
            {mob ? 'Publish' : 'Publish & Go Live'}
          </button>
        </div>
        <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Source type metadata
   ═══════════════════════════════════════════════════════════ */
const SOURCE_TYPES = [
  { value: 'file',      label: 'File Upload',   icon: 'upload_file',    iconColor: C.red,    placeholder: 'e.g. company_handbook.pdf',             subTemplate: s => `Uploaded file • ${s}` },
  { value: 'database',  label: 'SQL Database',   icon: 'database',       iconColor: C.indigo, placeholder: 'e.g. customer_db (SQL)',                 subTemplate: s => `Live connector • ${s}` },
  { value: 'web_crawl', label: 'Web Crawl',      icon: 'code',           iconColor: C.teal,   placeholder: 'e.g. api_docs.html',                    subTemplate: s => `Web crawl • ${s}` },
  { value: 'api',       label: 'REST API',       icon: 'api',            iconColor: '#8b5cf6', placeholder: 'e.g. crm_api_v2',                       subTemplate: s => `API endpoint • ${s}` },
  { value: 'csv',       label: 'CSV / Excel',    icon: 'table_chart',    iconColor: C.green,  placeholder: 'e.g. leads_export.csv',                 subTemplate: s => `Spreadsheet • ${s}` },
];

/* ═══════════════════════════════════════════════════════════
   Shared modal backdrop
   ═══════════════════════════════════════════════════════════ */
function Backdrop({ open, onClose, children, width = 540 }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,.45)', display: 'grid', placeItems: 'center', padding: 12, animation: 'fadeIn .15s' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.w, borderRadius: 16, width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 48px rgba(0,0,0,.15)', animation: 'slideUp .2s ease' }}>
        {children}
      </div>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Tab 1: Knowledge & RAG  — Full CRUD
   ═══════════════════════════════════════════════════════════ */
function KnowledgeTab({ mob, compact }) {
  const nextId = useRef(10);
  const [sources, setSources] = useState(INITIAL_SOURCES);

  /* RAG tuning */
  const [chunkSize, setChunkSize] = useState(512);
  const [overlap, setOverlap]     = useState(15);
  const [topK, setTopK]           = useState(5);

  /* Semantic search */
  const [searchQ, setSearchQ]       = useState('');
  const [searchDone, setSearchDone] = useState(false);
  const [searching, setSearching]   = useState(false);

  /* CRUD modals */
  const [showAdd, setShowAdd]       = useState(false);
  const [editSrc, setEditSrc]       = useState(null);   // source object or null
  const [deleteId, setDeleteId]     = useState(null);    // id or null
  const [menuId, setMenuId]         = useState(null);    // id for context menu

  /* Add / Edit form state */
  const [formType, setFormType] = useState('file');
  const [formName, setFormName] = useState('');
  const [formUrl, setFormUrl]   = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formFile, setFormFile] = useState(null);  // { name, size }
  const fileInputRef = useRef(null);

  const resetForm = () => { setFormType('file'); setFormName(''); setFormUrl(''); setFormDesc(''); setFormFile(null); };

  const openAdd = () => { resetForm(); setShowAdd(true); };
  const openEdit = (s) => {
    setFormType(s.type || 'file');
    setFormName(s.name);
    setFormUrl(s.url || '');
    setFormDesc(s.sub);
    setEditSrc(s);
    setMenuId(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    setFormFile({ name: file.name, size: `${sizeMB} MB` });
    if (!formName.trim()) setFormName(file.name);
    if (!formDesc.trim()) setFormDesc(`Uploaded file • ${sizeMB} MB`);
  };

  const typeMeta = SOURCE_TYPES.find(t => t.value === formType) || SOURCE_TYPES[0];

  const handleSave = () => {
    if (!formName.trim()) return;
    const sub = formDesc.trim() || typeMeta.subTemplate(formUrl.trim() || 'manual entry');
    if (editSrc) {
      /* Update */
      setSources(prev => prev.map(s => s.id === editSrc.id ? {
        ...s, name: formName.trim(), sub, type: formType, url: formUrl.trim(),
        icon: typeMeta.icon, iconColor: typeMeta.iconColor,
      } : s));
      setEditSrc(null);
    } else {
      /* Create */
      const newSrc = {
        id: nextId.current++, name: formName.trim(), sub,
        icon: typeMeta.icon, iconColor: typeMeta.iconColor,
        status: 'Queued', statusColor: C.t3, type: formType, url: formUrl.trim(),
      };
      setSources(prev => [...prev, newSrc]);
      /* Simulate indexing after 2s */
      setTimeout(() => {
        setSources(prev => prev.map(s => s.id === newSrc.id ? { ...s, status: 'Indexed', statusColor: C.green } : s));
      }, 2500);
      setShowAdd(false);
    }
    resetForm();
    if (editSrc) setEditSrc(null);
    if (showAdd) setShowAdd(false);
  };

  const handleDelete = () => {
    setSources(prev => prev.filter(s => s.id !== deleteId));
    setDeleteId(null);
  };

  const handleReindex = (id) => {
    setMenuId(null);
    setSources(prev => prev.map(s => s.id === id ? { ...s, status: 'Syncing', statusColor: C.amber } : s));
    setTimeout(() => {
      setSources(prev => prev.map(s => s.id === id ? { ...s, status: 'Indexed', statusColor: C.green } : s));
    }, 2000);
  };

  const runQuery = () => {
    if (!searchQ.trim()) return;
    setSearching(true); setSearchDone(false);
    setTimeout(() => { setSearching(false); setSearchDone(true); }, 900);
  };

  /* slider CSS */
  const sliderCSS = `
    input[type=range].rag-slider{-webkit-appearance:none;appearance:none;width:100%;height:6px;border-radius:99px;background:#e8e2df;outline:none;cursor:pointer}
    input[type=range].rag-slider::-webkit-slider-thumb{-webkit-appearance:none;width:16px;height:16px;border-radius:50%;background:${C.primary};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.15);cursor:pointer;transition:transform .15s}
    input[type=range].rag-slider::-webkit-slider-thumb:hover{transform:scale(1.2)}
    input[type=range].rag-slider::-moz-range-thumb{width:16px;height:16px;border-radius:50%;background:${C.primary};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.15);cursor:pointer}
  `;

  /* ── Form fields (shared between Add & Edit) ── */
  const renderForm = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Source type picker */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 700, color: C.t1, display: 'block', marginBottom: 10 }}>Source Type</label>
        <div style={{ display: 'grid', gridTemplateColumns: mob ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)', gap: 8 }}>
          {SOURCE_TYPES.map(t => {
            const sel = formType === t.value;
            return (
              <div key={t.value} onClick={() => setFormType(t.value)} style={{
                padding: '10px 6px', borderRadius: 10, cursor: 'pointer', textAlign: 'center',
                border: sel ? `2px solid ${C.primary}` : `1px solid ${C.bdr}`,
                background: sel ? C.pL : C.w, transition: 'all .15s',
              }}>
                <Icon name={t.icon} size={22} style={{ color: sel ? C.primary : t.iconColor, display: 'block', margin: '0 auto 6px' }} />
                <div style={{ fontSize: 10, fontWeight: 600, color: sel ? C.primary : C.t2 }}>{t.label}</div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Source name */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 700, color: C.t1, display: 'block', marginBottom: 6 }}>Source Name</label>
        <input value={formName} onChange={e => setFormName(e.target.value)}
          placeholder={typeMeta.placeholder}
          style={{ ...inputStyle }} {...focusHandlers} />
      </div>
      {/* URL / Connection string */}
      {formType !== 'file' && (
        <div>
          <label style={{ fontSize: 12, fontWeight: 700, color: C.t1, display: 'block', marginBottom: 6 }}>
            {formType === 'database' ? 'Connection String' : formType === 'api' ? 'API Endpoint URL' : 'URL'}
          </label>
          <input value={formUrl} onChange={e => setFormUrl(e.target.value)}
            placeholder={formType === 'database' ? 'postgresql://host:5432/db' : 'https://...'}
            style={{ ...inputStyle }} {...focusHandlers} />
        </div>
      )}
      {/* File drop zone for file/csv type */}
      {(formType === 'file' || formType === 'csv') && (
        <div>
          <input ref={fileInputRef} type="file"
            accept={formType === 'csv' ? '.csv,.xlsx,.xls,.tsv' : '.pdf,.docx,.txt,.json,.md'}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          {formFile ? (
            /* Selected file display */
            <div style={{
              border: `2px solid ${C.green}`, borderRadius: 12, padding: '16px 20px',
              background: `${C.green}08`, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: `${C.green}14`, display: 'grid', placeItems: 'center' }}>
                  <Icon name="description" size={20} style={{ color: C.green }} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{formFile.name}</div>
                  <div style={{ fontSize: 11, color: C.t3 }}>{formFile.size}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => fileInputRef.current?.click()} style={{
                  border: 'none', background: 'none', fontSize: 11, fontWeight: 700,
                  color: C.primary, cursor: 'pointer',
                }}>Change</button>
                <button onClick={() => setFormFile(null)} style={{
                  border: 'none', background: 'none', fontSize: 11, fontWeight: 700,
                  color: C.red, cursor: 'pointer',
                }}>Remove</button>
              </div>
            </div>
          ) : (
            /* Empty drop zone */
            <div onClick={() => fileInputRef.current?.click()} style={{
              border: `2px dashed ${C.bdr}`, borderRadius: 12, padding: '24px 20px',
              textAlign: 'center', background: '#faf9f8', cursor: 'pointer',
              transition: 'border-color .15s, background .15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.background = C.pL; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.bdr; e.currentTarget.style.background = '#faf9f8'; }}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.background = C.pL; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = C.bdr; e.currentTarget.style.background = '#faf9f8'; }}
              onDrop={e => {
                e.preventDefault();
                e.currentTarget.style.borderColor = C.bdr;
                e.currentTarget.style.background = '#faf9f8';
                const file = e.dataTransfer.files?.[0];
                if (file) {
                  const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
                  setFormFile({ name: file.name, size: `${sizeMB} MB` });
                  if (!formName.trim()) setFormName(file.name);
                  if (!formDesc.trim()) setFormDesc(`Uploaded file • ${sizeMB} MB`);
                }
              }}>
              <Icon name="cloud_upload" size={32} style={{ color: C.t3, marginBottom: 6 }} />
              <p style={{ fontSize: 12, color: C.t2, fontWeight: 600 }}>
                Drag & drop a file here, or <span style={{ color: C.primary, textDecoration: 'underline' }}>browse</span>
              </p>
              <p style={{ fontSize: 10, color: C.t3, marginTop: 4 }}>
                {formType === 'csv' ? 'CSV, XLSX, TSV up to 50MB' : 'PDF, DOCX, TXT, JSON, MD up to 25MB'}
              </p>
            </div>
          )}
        </div>
      )}
      {/* Description */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 700, color: C.t1, display: 'block', marginBottom: 6 }}>Description <span style={{ fontWeight: 400, color: C.t3 }}>(optional)</span></label>
        <input value={formDesc} onChange={e => setFormDesc(e.target.value)}
          placeholder="e.g. Syncing live connector • 45k rows"
          style={{ ...inputStyle }} {...focusHandlers} />
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{sliderCSS}</style>

      {/* ══ Top row: Sources + RAG Tuning ══ */}
      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '7fr 5fr', gap: compact ? 16 : 24 }}>

        {/* ── Connected Data Sources (CRUD list) ── */}
        <Card style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h4 style={{ fontSize: 15, fontWeight: 700 }}>Connected Data Sources</h4>
              <Pill bg={`${C.teal}14`} color={C.teal}>RAG Active</Pill>
            </div>
            <button onClick={openAdd} style={{
              border: 'none', background: 'none', color: C.primary,
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <Icon name="add" size={16} /> Add Source
            </button>
          </div>

          {/* Sources list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sources.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <Icon name="source" size={32} style={{ color: C.t3, marginBottom: 8 }} />
                <p style={{ fontSize: 13, color: C.t3 }}>No data sources connected yet.</p>
                <button onClick={openAdd} style={{ marginTop: 12, border: 'none', background: C.primary, color: '#fff', fontSize: 12, fontWeight: 700, padding: '8px 20px', borderRadius: 8, cursor: 'pointer' }}>
                  Add Your First Source
                </button>
              </div>
            )}
            {sources.map(s => (
              <div key={s.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 14px', background: C.bg, borderRadius: 10,
                border: `1px solid ${C.bdr}`, transition: 'border .15s',
              }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#d0ccc8'}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.bdr}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 6, background: C.w, display: 'grid', placeItems: 'center', border: `1px solid ${C.bdr}` }}>
                    <Icon name={s.icon} size={18} style={{ color: s.iconColor }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                    <div style={{ fontSize: 11, color: C.t3 }}>{s.sub}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
                  <Pill bg={`${s.statusColor}14`} color={s.statusColor}>{s.status}</Pill>
                  {/* More button */}
                  <div style={{ position: 'relative' }}>
                    <Icon name="more_vert" size={18}
                      style={{ color: C.t3, cursor: 'pointer', padding: 2 }}
                      onClick={() => setMenuId(menuId === s.id ? null : s.id)}
                    />
                    {/* Context menu */}
                    {menuId === s.id && (
                      <div style={{
                        position: 'absolute', top: '100%', right: 0, marginTop: 4,
                        background: C.w, borderRadius: 10, border: `1px solid ${C.bdr}`,
                        boxShadow: '0 8px 24px rgba(0,0,0,.12)', minWidth: 160,
                        zIndex: 100, overflow: 'hidden', animation: 'fadeIn .12s',
                      }}>
                        <button onClick={() => openEdit(s)} style={{
                          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                          padding: '10px 14px', border: 'none', background: 'none',
                          fontSize: 12, fontWeight: 600, color: C.t1, cursor: 'pointer',
                          transition: 'background .1s',
                        }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f5f3f2'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <Icon name="edit" size={16} style={{ color: C.t2 }} /> Edit Source
                        </button>
                        <button onClick={() => handleReindex(s.id)} style={{
                          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                          padding: '10px 14px', border: 'none', background: 'none',
                          fontSize: 12, fontWeight: 600, color: C.t1, cursor: 'pointer',
                          transition: 'background .1s',
                        }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f5f3f2'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <Icon name="sync" size={16} style={{ color: C.amber }} /> Re-index
                        </button>
                        <div style={{ height: 1, background: C.bdr }} />
                        <button onClick={() => { setDeleteId(s.id); setMenuId(null); }} style={{
                          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                          padding: '10px 14px', border: 'none', background: 'none',
                          fontSize: 12, fontWeight: 600, color: C.red, cursor: 'pointer',
                          transition: 'background .1s',
                        }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <Icon name="delete" size={16} /> Remove Source
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* ── RAG Engine Tuning ── */}
        <Card style={{ padding: 24 }}>
          <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 24 }}>RAG Engine Tuning</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>Chunk Size</label>
                <span style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 600, color: C.primary, background: C.pL, padding: '2px 8px', borderRadius: 4 }}>{chunkSize} Tokens</span>
              </div>
              <input type="range" className="rag-slider" min={128} max={2048} step={64}
                value={chunkSize} onChange={e => setChunkSize(+e.target.value)}
                style={{ width: '100%', height: 6, borderRadius: 99, background: `linear-gradient(to right, ${C.primary} ${((chunkSize - 128) / (2048 - 128)) * 100}%, #e8e2df ${((chunkSize - 128) / (2048 - 128)) * 100}%)` }}
              />
              <p style={{ fontSize: 10, color: C.t3, marginTop: 8 }}>Optimal for medium density documents like PDFs.</p>
            </div>

            {/* Context Overlap — range slider */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>Context Overlap</label>
                <span style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 600, color: C.primary, background: C.pL, padding: '2px 8px', borderRadius: 4 }}>{overlap}%</span>
              </div>
              <input type="range" className="rag-slider" min={0} max={50} step={1}
                value={overlap} onChange={e => setOverlap(+e.target.value)}
                style={{ width: '100%', height: 6, borderRadius: 99, background: `linear-gradient(to right, ${C.primary} ${(overlap / 50) * 100}%, #e8e2df ${(overlap / 50) * 100}%)` }}
              />
              <p style={{ fontSize: 10, color: C.t3, marginTop: 8 }}>Prevents loss of meaning across chunk boundaries.</p>
            </div>

            {/* Top-K Retrieval — number input */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>Top-K Retrieval</label>
                <span style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 600, color: C.primary, background: C.pL, padding: '2px 8px', borderRadius: 4 }}>{topK} Chunks</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input type="number" min={1} max={20} value={topK}
                  onChange={e => { const v = Math.max(1, Math.min(20, +e.target.value || 1)); setTopK(v); }}
                  style={{ width: 72, padding: '8px 10px', textAlign: 'center', borderRadius: 8, border: `1px solid ${C.bdr}`, fontSize: 13, fontWeight: 600, outline: 'none', background: '#faf9f8', fontFamily: 'inherit' }}
                />
                <p style={{ fontSize: 10, color: C.t3, flex: 1 }}>Number of highest-ranking segments sent to LLM.</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* ══ Semantic Search Preview ══ */}
      <Card style={{ padding: 24 }}>
        <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Semantic Search Preview</h4>

        {/* Search bar + button */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Icon name="search" size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: C.t3, pointerEvents: 'none' }} />
            <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && runQuery()}
              placeholder="Ask a question to test your knowledge base…"
              style={{ width: '100%', padding: '12px 16px 12px 42px', borderRadius: 10, border: `1px solid ${C.bdr}`, fontSize: 13, outline: 'none', background: '#faf9f8', transition: 'border .15s' }}
              onFocus={e => e.target.style.borderColor = C.primary}
              onBlur={e => e.target.style.borderColor = C.bdr}
            />
          </div>
          <button onClick={runQuery} style={{
            padding: '0 24px', borderRadius: 10, border: 'none', background: C.teal, color: '#fff',
            fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            transition: 'opacity .15s', whiteSpace: 'nowrap', opacity: searchQ.trim() ? 1 : 0.5,
          }}>
            <Icon name="analytics" size={18} /> Test Query
          </button>
        </div>
        <div style={{ background: C.bg, borderRadius: 12, padding: 20, border: `1px dashed ${C.bdr}`, minHeight: 160 }}>
          {!searchDone && !searching && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 120, gap: 8 }}>
              <Icon name="manage_search" size={32} style={{ color: C.t3 }} />
              <p style={{ fontSize: 12, color: C.t3 }}>Enter a query above and hit <b>Test Query</b> to see retrieved chunks</p>
            </div>
          )}

          {/* Searching */}
          {searching && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 120, gap: 8 }}>
              <Icon name="hourglass_empty" size={28} style={{ color: C.primary, animation: 'spin 1s linear infinite' }} />
              <p style={{ fontSize: 12, color: C.t2, fontWeight: 600 }}>Searching knowledge base…</p>
              <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
            </div>
          )}

          {/* Results */}
          {searchDone && !searching && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.08em' }}>Retrieved Context Chunks</span>
                <span style={{ fontSize: 11, color: C.t2 }}>Total Latency: <b>450ms</b></span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 14 }}>
                {[
                  { src: 'company_handbook.pdf', page: 12, score: 0.98, text: '\u201c…our standard vacation policy allows for 25 days of paid time off per calendar year for full-time employees, accruable on a monthly basis…\u201d' },
                  { src: 'company_handbook.pdf', page: 14, score: 0.89, text: '\u201c…unspent leave may be carried over up to 5 days, subject to manager approval and specific regional labor laws…\u201d' },
                  { src: 'api_docs.html', page: 3, score: 0.76, text: '\u201c…the /v2/employees endpoint returns detailed leave balances including accrued, used, and remaining days for the current fiscal period…\u201d' },
                  { src: 'customer_db_v2 (SQL)', page: null, score: 0.61, text: '\u201c…SELECT employee_id, leave_type, days_remaining FROM leave_balances WHERE status = active ORDER BY days_remaining DESC…\u201d' },
                ].slice(0, topK > 4 ? 4 : topK).map((chunk, i) => {
                  const ext = chunk.src.includes('.pdf') ? 'pdf' : chunk.src.includes('.html') ? 'html' : 'SQL';
                  const iconMap = { pdf: 'picture_as_pdf', html: 'code', SQL: 'database' };
                  const colorMap = { pdf: C.red, html: '#f97316', SQL: C.indigo };
                  return (
                    <div key={i} style={{ padding: 16, background: C.w, borderRadius: 10, border: `1px solid ${C.bdr}`, position: 'relative', overflow: 'hidden', opacity: 1 - i * 0.08, boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
                      <div style={{ position: 'absolute', top: 0, right: 0, background: C.teal, color: '#fff', fontSize: 9, fontWeight: 700, padding: '3px 8px', borderBottomLeftRadius: 8 }}>
                        Score: {chunk.score.toFixed(2)}
                      </div>
                      {/* Source */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        <Icon name={iconMap[ext] || 'description'} size={14} style={{ color: colorMap[ext] || C.t3 }} />
                        <span style={{ fontSize: 10, fontWeight: 700, color: C.t2 }}>{chunk.src}{chunk.page ? ` (pg ${chunk.page})` : ''}</span>
                      </div>
                      <p style={{ fontSize: 12, color: C.t2, lineHeight: 1.6, fontStyle: 'italic' }}>{chunk.text}</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </Card>

      {/* ══════════════════════════════════════
          ADD SOURCE MODAL
         ══════════════════════════════════════ */}
      <Backdrop open={showAdd} onClose={() => setShowAdd(false)}>
        <div style={{ padding: '24px 28px 20px', borderBottom: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 2 }}>Add Data Source</h3>
            <p style={{ fontSize: 12, color: C.t2 }}>Connect a new data source to your RAG knowledge base.</p>
          </div>
          <Icon name="close" size={20} style={{ color: C.t3, cursor: 'pointer' }} onClick={() => setShowAdd(false)} />
        </div>
        <div style={{ padding: '24px 28px' }}>
          {renderForm()}
        </div>
        <div style={{ padding: '16px 28px 24px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={() => setShowAdd(false)} style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 13, fontWeight: 700, color: C.t2, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={!formName.trim()} style={{
            padding: '10px 24px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            background: formName.trim() ? C.primary : '#ccc', color: '#fff', transition: 'background .15s',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="add" size={16} /> Add Source
            </span>
          </button>
        </div>
      </Backdrop>

      {/* ══════════════════════════════════════
          EDIT SOURCE MODAL
         ══════════════════════════════════════ */}
      <Backdrop open={!!editSrc} onClose={() => { setEditSrc(null); resetForm(); }}>
        <div style={{ padding: '24px 28px 20px', borderBottom: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 2 }}>Edit Data Source</h3>
            <p style={{ fontSize: 12, color: C.t2 }}>Modify the connection details for this source.</p>
          </div>
          <Icon name="close" size={20} style={{ color: C.t3, cursor: 'pointer' }} onClick={() => { setEditSrc(null); resetForm(); }} />
        </div>
        <div style={{ padding: '24px 28px' }}>
          {renderForm()}
        </div>
        <div style={{ padding: '16px 28px 24px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={() => { setEditSrc(null); resetForm(); }} style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 13, fontWeight: 700, color: C.t2, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={!formName.trim()} style={{
            padding: '10px 24px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            background: formName.trim() ? C.primary : '#ccc', color: '#fff',
          }}>
            Save Changes
          </button>
        </div>
      </Backdrop>

      {/* ══════════════════════════════════════
          DELETE CONFIRMATION MODAL
         ══════════════════════════════════════ */}
      <Backdrop open={deleteId !== null} onClose={() => setDeleteId(null)} width={420}>
        <div style={{ padding: '28px 28px 24px', textAlign: 'center' }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: `${C.red}14`, display: 'grid', placeItems: 'center', margin: '0 auto 16px' }}>
            <Icon name="delete" size={26} style={{ color: C.red }} />
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>Remove Data Source?</h3>
          <p style={{ fontSize: 13, color: C.t2, lineHeight: 1.6 }}>
            This will disconnect <b>{sources.find(s => s.id === deleteId)?.name}</b> from your knowledge base. All indexed chunks will be deleted. This action cannot be undone.
          </p>
        </div>
        <div style={{ padding: '0 28px 24px', display: 'flex', gap: 10 }}>
          <button onClick={() => setDeleteId(null)} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 13, fontWeight: 700, color: C.t2, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleDelete} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: C.red, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            Remove Source
          </button>
        </div>
      </Backdrop>

      {/* Close menu on outside click */}
      {menuId !== null && (
        <div onClick={() => setMenuId(null)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Toggle switch component
   ═══════════════════════════════════════════════════════════ */
function Toggle({ on, onToggle }) {
  return (
    <div onClick={onToggle} style={{
      width: 44, height: 24, borderRadius: 99, cursor: 'pointer',
      background: on ? C.primary : '#d0ccc8', position: 'relative',
      transition: 'background .2s', flexShrink: 0,
    }}>
      <div style={{
        width: 20, height: 20, borderRadius: '50%', background: '#fff',
        position: 'absolute', top: 2,
        left: on ? 22 : 2,
        transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
      }} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Tab 2: Behavior & Prompt  (redesigned to match reference)
   ═══════════════════════════════════════════════════════════ */
const PROMPT_MAP = {
  'Support Specialist': `# System Prompt: Customer Service Agent
Role: You are Nieramind's official Support Specialist.
Objective: Resolve user queries regarding product setup and billing with high empathy.

Guidelines:
1. Always verify identity before discussing billing details.
2. Use professional, warm, and concise language.
3. If unsure, escalate to "human_agent_transfer".
4. Never mention internal database IDs or system architecture.

[Behavior Overrides]
- Context window: 128k tokens
- Style: Corporate friendly`,

  'Sales Closer': `# System Prompt: Sales Closer Agent
Role: You are Nieramind's high-performance Sales Closer.
Objective: Convert warm leads into paying customers by highlighting product value and handling objections.

Guidelines:
1. Open with a personalized greeting referencing the lead's company or role.
2. Present the top 3 product benefits within the first 2 messages.
3. Handle objections with empathy, then pivot to ROI data.
4. Always propose a concrete next step (demo, trial, call).
5. If the lead goes cold, schedule a follow-up via CTA.

[Behavior Overrides]
- Context window: 128k tokens
- Style: Persuasive yet professional
- Tone: Confident, data-driven`,

  'Technical Guide': `# System Prompt: Technical Guide
Role: You are Nieramind's Technical Support Engineer.
Objective: Help developers and technical users troubleshoot issues, integrate APIs, and understand system architecture.

Guidelines:
1. Ask clarifying questions about the user's environment (OS, SDK version, language).
2. Provide code snippets in fenced markdown blocks.
3. Link to official documentation when available.
4. Escalate to engineering if the issue involves a known bug (tag: "eng_escalation").
5. Never share internal credentials, tokens, or infrastructure details.

[Behavior Overrides]
- Context window: 128k tokens
- Style: Technical, precise
- Format: Markdown with code blocks`,

  'General Assistant': `# System Prompt: General Assistant
Role: You are Nieramind's versatile General Assistant.
Objective: Assist users with a wide range of tasks including scheduling, research, writing, and Q&A.

Guidelines:
1. Adapt tone to match the user's formality level.
2. For factual questions, cite sources when possible.
3. For creative tasks, ask about preferences before generating.
4. Keep responses concise unless the user asks for detail.
5. If a request is outside your capabilities, explain clearly and suggest alternatives.

[Behavior Overrides]
- Context window: 128k tokens
- Style: Adaptive, helpful
- Tone: Warm, conversational`,
};

const PROMPT_TEMPLATES = Object.keys(PROMPT_MAP);

const MODELS = [
  { id: 'gpt4o',        name: 'GPT-4o',           provider: 'OpenAI',    tier: 'Standard',   desc: 'Best balance of speed and intelligence.',       icon: 'cognition',  iconBg: '#1a1a1a',  iconColor: '#fff'  },
  { id: 'gpt4o-mini',   name: 'GPT-4o Mini',      provider: 'OpenAI',    tier: 'Economy',    desc: 'Fast, cost-effective for simple tasks.',         icon: 'bolt',       iconBg: '#1a1a1a',  iconColor: '#fff'  },
  { id: 'claude-sonnet', name: 'Claude Sonnet 4',  provider: 'Anthropic', tier: 'Standard',   desc: 'Strong reasoning with balanced latency.',       icon: 'psychology', iconBg: '#d97706',  iconColor: '#fff'  },
  { id: 'claude-haiku',  name: 'Claude Haiku 3.5', provider: 'Anthropic', tier: 'Economy',    desc: 'Ultra-fast for high-volume routing.',            icon: 'speed',      iconBg: '#d97706',  iconColor: '#fff'  },
  { id: 'gemini-pro',    name: 'Gemini 2.5 Pro',   provider: 'Google',    tier: 'Premium',    desc: 'Multimodal with long context windows.',          icon: 'auto_awesome', iconBg: '#4285f4', iconColor: '#fff' },
  { id: 'llama3',        name: 'Llama 3.1 70B',    provider: 'Meta',      tier: 'Open Source', desc: 'Self-hosted, full data sovereignty.',           icon: 'dns',        iconBg: '#6366f1',  iconColor: '#fff'  },
];

function BehaviorTab({ mob, compact }) {
  const [template, setTemplate]   = useState('Support Specialist');
  const [prompt, setPrompt]       = useState(PROMPT_MAP['Support Specialist']);
  const [showTPL, setShowTPL]     = useState(false);
  const [temp, setTemp]           = useState(0.7);
  const [strictData, setStrictData] = useState(true);
  const [maxTokens, setMaxTokens] = useState(1024);
  const [pii, setPii]             = useState(true);
  const [hateSpeech, setHateSpeech] = useState(true);
  const [profanity, setProfanity] = useState(false);
  const [ipBlock, setIpBlock]     = useState(false);

  /* Model switcher */
  const [selectedModel, setSelectedModel] = useState('gpt4o');
  const [showModelModal, setShowModelModal] = useState(false);
  const [pendingModel, setPendingModel]   = useState('gpt4o');
  const activeModel = MODELS.find(m => m.id === selectedModel) || MODELS[0];

  const handleTemplateSelect = (t) => {
    setTemplate(t);
    setPrompt(PROMPT_MAP[t]);
    setShowTPL(false);
  };

  const handleModelConfirm = () => {
    setSelectedModel(pendingModel);
    setShowModelModal(false);
  };

  const sliderPct = temp * 100;

  const sliderCSS2 = `
    input[type=range].temp-slider{-webkit-appearance:none;appearance:none;width:100%;height:6px;border-radius:99px;outline:none;cursor:pointer}
    input[type=range].temp-slider::-webkit-slider-thumb{-webkit-appearance:none;width:16px;height:16px;border-radius:50%;background:${C.primary};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.15);cursor:pointer;transition:transform .15s}
    input[type=range].temp-slider::-webkit-slider-thumb:hover{transform:scale(1.2)}
    input[type=range].temp-slider::-moz-range-thumb{width:16px;height:16px;border-radius:50%;background:${C.primary};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.15);cursor:pointer}
  `;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{sliderCSS2}</style>

      {/* ══ 1. System Instructions ══ */}
      <Card style={{ overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${C.bdr}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon name="terminal" size={22} style={{ color: C.primary }} />
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>System Instructions</h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.06em' }}>Template:</span>
            <button onClick={() => setShowTPL(!showTPL)} style={{
              padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              border: `1px solid ${C.bdr}`, background: C.w, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              {template}
              <Icon name="expand_more" size={16} style={{ color: C.t2 }} />
            </button>
            {showTPL && (
              <>
                <div onClick={() => setShowTPL(false)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 51,
                  background: C.w, borderRadius: 10, border: `1px solid ${C.bdr}`,
                  boxShadow: '0 8px 24px rgba(0,0,0,.12)', minWidth: 190, overflow: 'hidden',
                }}>
                  {PROMPT_TEMPLATES.map(t => (
                    <button key={t} onClick={() => handleTemplateSelect(t)} style={{
                      display: 'block', width: '100%', padding: '10px 14px', border: 'none',
                      background: template === t ? C.pL : 'transparent', fontSize: 12, fontWeight: 600,
                      color: template === t ? C.primary : C.t1, cursor: 'pointer', textAlign: 'left',
                      transition: 'background .1s',
                    }}
                      onMouseEnter={e => { if (template !== t) e.currentTarget.style.background = '#f5f3f2'; }}
                      onMouseLeave={e => { if (template !== t) e.currentTarget.style.background = 'transparent'; }}>
                      {t}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Editable prompt textarea */}
        <div style={{ padding: 0 }}>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            spellCheck={false}
            style={{
              width: '100%', minHeight: 260, padding: '20px 24px',
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Courier New', monospace",
              fontSize: 13, lineHeight: 1.8, color: C.t1,
              background: '#faf9f8', border: 'none', outline: 'none', resize: 'vertical',
            }}
          />
        </div>

        {/* Footer bar */}
        <div style={{
          padding: '8px 24px', background: '#f1eeec',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: 11, color: C.t3 }}>Character count: {prompt.length}</span>
          <span style={{ fontSize: 11, color: C.t3, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icon name="bolt" size={14} style={{ color: C.primary }} /> AI Optimized
          </span>
        </div>
      </Card>

      {/* ══ 2. Response Behavior + Security & Safety (2-col) ══ */}
      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '1fr 1fr', gap: compact ? 16 : 24 }}>

        {/* ── Response Behavior ── */}
        <Card style={{ padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
            <Icon name="tune" size={22} style={{ color: C.primary }} />
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Response Behavior</h3>
          </div>

          {/* Temperature slider */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Creativity (Temperature)</label>
              <span style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 600, color: C.primary, background: C.pL, padding: '2px 8px', borderRadius: 4 }}>
                {temp.toFixed(1)}
              </span>
            </div>
            <input type="range" className="temp-slider" min={0} max={1} step={0.1}
              value={temp} onChange={e => setTemp(+e.target.value)}
              style={{ width: '100%', height: 6, borderRadius: 99, background: `linear-gradient(to right, ${C.primary} ${sliderPct}%, #e8e2df ${sliderPct}%)` }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              {['Precise', 'Balanced', 'Creative'].map(l => (
                <span key={l} style={{ fontSize: 9, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.05em' }}>{l}</span>
              ))}
            </div>
          </div>

          {/* Strict Data Adherence */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px', background: C.bg, borderRadius: 10, marginBottom: 20,
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Strict Data Adherence</div>
              <div style={{ fontSize: 11, color: C.t3 }}>Restrict model to provided knowledge base only.</div>
            </div>
            <Toggle on={strictData} onToggle={() => setStrictData(!strictData)} />
          </div>

          {/* Max Response Length */}
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>Max Response Length</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="number" min={64} max={8192} value={maxTokens}
                onChange={e => setMaxTokens(Math.max(64, Math.min(8192, +e.target.value || 64)))}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 10,
                  border: `1px solid ${C.bdr}`, fontSize: 13, outline: 'none',
                  fontFamily: 'inherit', background: '#faf9f8',
                }}
                onFocus={e => { e.target.style.borderColor = C.primary; e.target.style.boxShadow = `0 0 0 3px ${C.pL}`; }}
                onBlur={e => { e.target.style.borderColor = C.bdr; e.target.style.boxShadow = 'none'; }}
              />
              <span style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase' }}>Tokens</span>
            </div>
          </div>
        </Card>

        {/* ── Security & Safety ── */}
        <Card style={{ padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
            <Icon name="shield" size={22} style={{ color: C.primary }} />
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Security & Safety</h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { icon: 'masks',   label: 'PII Masking',            desc: 'Redact emails, phones, and names.',            on: pii,        toggle: () => setPii(!pii) },
              { icon: 'gavel',   label: 'Hate Speech Filtering',  desc: 'Auto-block toxic incoming/outgoing msgs.',     on: hateSpeech, toggle: () => setHateSpeech(!hateSpeech) },
              { icon: 'block',   label: 'Profanity Filter',       desc: 'Mask or remove profane language.',              on: profanity,  toggle: () => setProfanity(!profanity) },
              { icon: 'language', label: 'IP & Geo Blocking',     desc: 'Restrict access by region or IP range.',       on: ipBlock,    toggle: () => setIpBlock(!ipBlock) },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 14px', borderRadius: 10,
                border: `1px solid ${C.bdr}`, transition: 'border-color .15s',
              }}
                onMouseEnter={e => e.currentTarget.style.borderColor = `${C.primary}40`}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.bdr}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Icon name={item.icon} size={20} style={{ color: C.t2 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 1 }}>{item.label}</div>
                    <div style={{ fontSize: 11, color: C.t3 }}>{item.desc}</div>
                  </div>
                </div>
                <Toggle on={item.on} onToggle={item.toggle} />
              </div>
            ))}
          </div>

          {/* Info banner */}
          <div style={{
            marginTop: 16, padding: '12px 14px', borderRadius: 10,
            background: C.pL, borderLeft: `4px solid ${C.primary}50`,
            display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <Icon name="info" size={18} style={{ color: C.primary, flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: 11, color: C.t1, lineHeight: 1.6 }}>
              Safety filters are applied at the proxy layer for maximum compliance. Data is encrypted at rest.
            </p>
          </div>
        </Card>
      </div>

      {/* ══ 3. Bottom cards: Pro Tip + Model Version ══ */}
      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '1fr 1fr', gap: compact ? 16 : 24 }}>
        {/* Pro Tip */}
        <div style={{
          padding: 20, borderRadius: 14,
          background: 'linear-gradient(135deg, #fff, #fdf8f6)',
          border: `1px solid ${C.bdr}`, display: 'flex', gap: 14,
        }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: C.pL, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon name="lightbulb" size={22} style={{ color: C.primary }} />
          </div>
          <div>
            <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Pro Tip: Iterative Prompts</h4>
            <p style={{ fontSize: 11, color: C.t2, lineHeight: 1.6 }}>
              Small changes in the "Objective" section often yield better results than complex formatting. Start simple and add constraints only as needed.
            </p>
          </div>
        </div>

        {/* Model Version */}
        <Card style={{
          padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: activeModel.iconBg, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              <Icon name={activeModel.icon} size={22} style={{ color: activeModel.iconColor }} />
            </div>
            <div>
              <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Model Version</h4>
              <p style={{ fontSize: 12, color: C.t2 }}>Current: <span style={{ color: C.t1, fontWeight: 600 }}>{activeModel.name} ({activeModel.tier})</span></p>
            </div>
          </div>
          <button onClick={() => { setPendingModel(selectedModel); setShowModelModal(true); }} style={{
            border: 'none', background: 'none', fontSize: 11, fontWeight: 700,
            color: C.primary, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '.04em',
          }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
            Switch Model
          </button>
        </Card>
      </div>

      {/* ══════════════════════════════════════
          SWITCH MODEL MODAL
         ══════════════════════════════════════ */}
      <Backdrop open={showModelModal} onClose={() => setShowModelModal(false)} width={580}>
        <div style={{ padding: '24px 28px 16px', borderBottom: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 2 }}>Switch AI Model</h3>
            <p style={{ fontSize: 12, color: C.t2 }}>Choose the LLM powering your agent's responses.</p>
          </div>
          <Icon name="close" size={20} style={{ color: C.t3, cursor: 'pointer' }} onClick={() => setShowModelModal(false)} />
        </div>
        <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {MODELS.map(m => {
            const sel = pendingModel === m.id;
            return (
              <div key={m.id} onClick={() => setPendingModel(m.id)} style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
                borderRadius: 12, cursor: 'pointer', transition: 'all .15s',
                border: sel ? `2px solid ${C.primary}` : `1px solid ${C.bdr}`,
                background: sel ? `${C.primary}06` : C.w,
              }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: m.iconBg, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  <Icon name={m.icon} size={20} style={{ color: m.iconColor }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{m.name}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, color: C.t3, background: '#f1eeec', padding: '2px 6px', borderRadius: 4, textTransform: 'uppercase' }}>{m.tier}</span>
                    {selectedModel === m.id && (
                      <span style={{ fontSize: 9, fontWeight: 700, color: C.green, background: `${C.green}14`, padding: '2px 6px', borderRadius: 4 }}>CURRENT</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: C.t2 }}>{m.provider} — {m.desc}</div>
                </div>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                  border: sel ? `6px solid ${C.primary}` : `2px solid ${C.bdr}`,
                  transition: 'border .15s',
                }} />
              </div>
            );
          })}
        </div>
        <div style={{ padding: '16px 28px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ fontSize: 11, color: C.t3, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icon name="info" size={14} style={{ color: C.t3 }} />
            Switching models may affect response style and latency.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setShowModelModal(false)} style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${C.bdr}`, background: C.w, fontSize: 13, fontWeight: 700, color: C.t2, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleModelConfirm} style={{
              padding: '10px 24px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              background: pendingModel !== selectedModel ? C.primary : '#ccc',
              color: '#fff', transition: 'background .15s',
            }}>
              Confirm Switch
            </button>
          </div>
        </div>
      </Backdrop>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Tab 3: Integrations  (redesigned to match reference)
   ═══════════════════════════════════════════════════════════ */
const EMBED_CODE = `<script src="https://cdn.nieramind.ai/widget/v1/loader.js" data-agent-id="agent_92a1_k31b_08" data-theme="light" async></script>`;
const API_KEY_RAW = 'sk_live_51M7fXpL9K0z9R2xX4q3m1p0b9s8d7f6';

function IntegrationsTab({ mob, compact }) {
  const [copied, setCopied]       = useState(false);
  const [showKey, setShowKey]     = useState(false);
  const [apiKey, setApiKey]       = useState(API_KEY_RAW);
  const [regenConfirm, setRegenConfirm] = useState(false);
  const [channels, setChannels]   = useState(CHANNELS);

  const handleCopy = () => {
    navigator.clipboard.writeText(EMBED_CODE).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      /* fallback */
      const ta = document.createElement('textarea');
      ta.value = EMBED_CODE; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRegen = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let key = 'sk_live_';
    for (let i = 0; i < 32; i++) key += chars.charAt(Math.floor(Math.random() * chars.length));
    setApiKey(key);
    setRegenConfirm(false);
    setShowKey(true);
  };

  const toggleChannel = (id) => {
    setChannels(prev => prev.map(ch => ch.id === id && !ch.badge ? { ...ch, active: !ch.active } : ch));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>

      {/* ══ 1. Web Chat Embed ══ */}
      <section>
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Web Chat Embed</h3>
          <p style={{ fontSize: 13, color: C.t2 }}>
            Add the Nieramind.AI chat widget to your website by pasting this script tag before the closing <code style={{ fontWeight: 600 }}>&lt;/body&gt;</code> tag.
          </p>
        </div>
        <Card style={{ overflow: 'hidden' }}>
          {/* Dark code block */}
          <div style={{
            background: '#1a1714', padding: mob ? '16px 14px' : '24px 28px', position: 'relative',
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Courier New', monospace",
            fontSize: 13, lineHeight: 1.8, color: '#c8c0b8',
          }}>
            <code style={{ wordBreak: 'break-all', fontSize: mob ? 11 : 13 }}>
              {EMBED_CODE}
            </code>
            <button onClick={handleCopy} style={{
              position: 'absolute', top: 16, right: 16,
              background: copied ? C.green : C.primary, color: '#fff',
              border: 'none', borderRadius: 8, padding: '8px 16px',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              boxShadow: '0 2px 8px rgba(0,0,0,.2)', transition: 'background .15s',
            }}>
              <Icon name={copied ? 'check' : 'content_copy'} size={14} />
              {copied ? 'Copied!' : 'Copy Code'}
            </button>
          </div>
          {/* Tip bar */}
          <div style={{
            padding: '12px 20px', background: '#faf9f8',
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, color: C.t3, fontStyle: 'italic',
          }}>
            <Icon name="info" size={14} style={{ color: C.t3, flexShrink: 0 }} />
            Tip: You can customize the widget theme and language in the Behavior settings.
          </div>
        </Card>
      </section>

      {/* ══ 2. REST API Access ══ */}
      <section>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>REST API Access</h3>
        <Card style={{ padding: 24 }}>
          <p style={{ fontSize: 13, color: C.t2, marginBottom: 20 }}>
            Use this API key to integrate Nieramind.AI with your own applications and back-end services.
          </p>
          <div style={{ display: 'flex', flexDirection: mob ? 'column' : 'row', gap: mob ? 10 : 14, alignItems: mob ? 'stretch' : 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
                Private API Key
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  readOnly
                  type={showKey ? 'text' : 'password'}
                  value={apiKey}
                  style={{
                    width: '100%', padding: '12px 48px 12px 16px', borderRadius: 10,
                    border: `1px solid ${C.bdr}`, fontSize: 13,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Courier New', monospace",
                    color: C.t1, outline: 'none', background: '#faf9f8',
                  }}
                />
                <button onClick={() => setShowKey(!showKey)} style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                  color: showKey ? C.primary : C.t3, transition: 'color .15s',
                }}>
                  <Icon name={showKey ? 'visibility_off' : 'visibility'} size={20} />
                </button>
              </div>
            </div>
            <button onClick={() => setRegenConfirm(true)} style={{
              height: 46, padding: '0 24px', borderRadius: 10,
              border: `2px solid ${C.primary}`, background: C.w,
              fontSize: 13, fontWeight: 700, color: C.primary,
              cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'background .15s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = C.pL}
              onMouseLeave={e => e.currentTarget.style.background = C.w}>
              Regenerate Key
            </button>
          </div>
        </Card>
      </section>

      {/* ══ 3. Channel Integrations ══ */}
      <section>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Channel Integrations</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {channels.map(ch => {
            const disabled = !!ch.badge;
            return (
              <Card key={ch.id} style={{
                padding: '16px 20px', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', opacity: disabled ? 0.55 : 1,
                transition: 'opacity .15s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{
                    width: mob ? 40 : 48, height: mob ? 40 : 48, borderRadius: 10,
                    background: ch.active ? '#ecfdf5' : '#f1eeec',
                    display: 'grid', placeItems: 'center',
                  }}>
                    <Icon name={ch.icon} size={26} style={{ color: ch.active ? '#059669' : C.t3 }} />
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <h4 style={{ fontSize: 13, fontWeight: 700 }}>{ch.name}</h4>
                      {ch.badge && (
                        <span style={{
                          fontSize: 9, fontWeight: 700, color: C.t2,
                          background: '#e8e2df', padding: '2px 8px',
                          borderRadius: 99, textTransform: 'uppercase',
                        }}>
                          {ch.badge}
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: C.t3 }}>{ch.desc}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {ch.active && !disabled && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: '#059669',
                      background: '#ecfdf5', padding: '3px 8px',
                      borderRadius: 4, textTransform: 'uppercase', letterSpacing: '.04em',
                    }}>
                      Active
                    </span>
                  )}
                  <Toggle on={ch.active} onToggle={disabled ? undefined : () => toggleChannel(ch.id)} />
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* ══ Regenerate Key Confirmation Modal ══ */}
      <Backdrop open={regenConfirm} onClose={() => setRegenConfirm(false)} width={440}>
        <div style={{ padding: '28px 28px 24px', textAlign: 'center' }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: `${C.amber}18`, display: 'grid', placeItems: 'center', margin: '0 auto 16px' }}>
            <Icon name="warning" size={26} style={{ color: C.amber }} />
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>Regenerate API Key?</h3>
          <p style={{ fontSize: 13, color: C.t2, lineHeight: 1.6 }}>
            This will <b>permanently invalidate</b> your current key. Any existing integrations using the old key will stop working immediately.
          </p>
        </div>
        <div style={{ padding: '0 28px 24px', display: 'flex', gap: 10 }}>
          <button onClick={() => setRegenConfirm(false)} style={{
            flex: 1, padding: '10px 0', borderRadius: 8,
            border: `1px solid ${C.bdr}`, background: C.w,
            fontSize: 13, fontWeight: 700, color: C.t2, cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={handleRegen} style={{
            flex: 1, padding: '10px 0', borderRadius: 8,
            border: 'none', background: C.primary, color: '#fff',
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>Regenerate</button>
        </div>
      </Backdrop>
    </div>
  );
}
